/* eslint-disable no-shadow */
import React from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { getRatingDetails, setRatingChoice, getPickleBallRating, getMatchTypeIndicator } from '../../../utils';
import { getLabelByTypeId } from '../../../utils/types';
import { sportTypeIds } from '../../../utils/eventConstants';
import { MatchFormatTypes } from '../../../utils/postScoreConstants';
import { RatingStatuses, RatingDisplayStates } from '../../../utils/constants';
import withAuth from '../../hocs/withAuth';

const MONTH_ABBREVIATIONS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatMonthAndShortYear = (dateString) => {
    const date = new Date(dateString);
    // The caller enters this branch on the presence of the rating, not the date (see
    // applyHistoricRating's entry guard), so a historic row can legitimately arrive here with a
    // null/absent date - guard rather than let an Invalid Date's `getMonth()`/`getFullYear()` render
    // the literal string `undefined 'aN`.
    // The falsy check is not redundant with the NaN check: `new Date(null)` coerces to the epoch, so
    // a null date is a *valid* Date reading `Jan '70` rather than an Invalid one. Null is the shape
    // the payload actually emits (the production probe recorded `historicSinglesRating: null` with a
    // sibling display field still populated), so it is the case that matters most here.
    if (!dateString || Number.isNaN(date.getTime())) {
        return '';
    }
    return `${MONTH_ABBREVIATIONS[date.getMonth()]} '${date.getFullYear().toString().slice(-2)}`;
};

// Sub-label per display state. `fullyReliable` and `unrated` are absent on purpose: those two rows
// carry no sub-label, and a state this map does not name renders none either - which keeps the
// states TMS-2226 does not describe rendering as they do today.
const RATING_SUB_LABELS = {
    [RatingDisplayStates.PROJECTED]: rating => (rating.progress ? `${rating.progress}% Reliable` : ''),
    [RatingDisplayStates.ESTIMATED]: () => RatingStatuses.ESTIMATED,
    [RatingDisplayStates.HISTORIC]: rating => formatMonthAndShortYear(rating.historicDate),
};

// Reads the decision `getRatingDetails` already made for the number, instead of re-deriving it from
// the raw player. The old second derivation ordered the estimated range ahead of a historic rating
// while the number slot ordered them the other way round, so a historic rating rendered under an
// "Estimated" label (TMS-2226).
const getDisplayStateLabel = (rating) => {
    const toLabel = RATING_SUB_LABELS[rating.displayState];
    return toLabel ? toLabel(rating) : '';
};

const UtrLabel = (props) => {
    /** force:
     * - ratingType: singles/doubles
     * - utrType: sanctioned/myUtr
     * - typeDots: true (show singles or doubles dots after utr label)
     * - showReliability: true (render the sub-label for the rating's resolved display state -
     *   a reliability percentage while still climbing to 100%, Estimated, a historic date, or
     *   no label at all once fully reliable or unrated)
     */

    const { player, sportTypeId = sportTypeIds.TENNIS, isPickleballRating, hideReliableLabelForPkb, isItGlobalSearch, 'data-testid': dataTestId } = props;
    const { ratingType, utrType, hideUtrLabel, typeDots, showReliability } = props.force;
    const isPickleBallSportType = sportTypeId === sportTypeIds.PICKLEBALL;

    const singlePkbRatings = player.singlespkbrating || player.historicpkbsinglesrating || player.singlesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const doublesPkbRatings = player.doublespkbrating || player.historicpkbdoublesrating || player.doublesPkbRatingDisplay?.pkbRating || player.utrRange?.pkbRatingDisplay;
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const utrTypes = coreUI?.types?.tms?.utrTypes;

    const rating = getRatingDetails(setRatingChoice({ player, ratingType, utrType }));
    const estimatedRating = 'Estimated';

    const getHistoricRatingLabel = () => {
        if ((rating.status === 'Unrated' || rating.status === 'Inactive') && rating.utrRange && !isPickleballRating) {
            if (rating.utrRange.minUtr && rating.utrRange.maxUtr) {
                return estimatedRating;
            }
        }
        if (ratingType === MatchFormatTypes.SINGLES && player.historicRatings?.historicSinglesRating && !player.singlesUtr && !isPickleballRating) {
            return formatMonthAndShortYear(player.historicRatings.historicSinglesRatingDate);
        }
        if (ratingType === MatchFormatTypes.DOUBLES && player?.historicRatings?.historicDoublesRating && !player.doublesUtr && !isPickleballRating) {
            return formatMonthAndShortYear(player.historicRatings.historicDoublesRatingDate);
        }
        if (ratingType === MatchFormatTypes.SINGLES && player?.historicPkbSinglesRating && !player.singlesPkbRating && !!isPickleballRating) {
            return formatMonthAndShortYear(player?.historicPkbSinglesRatingDate);
        }
        if (ratingType === MatchFormatTypes.DOUBLES && player?.historicPkbDoublesRating && !player.doublesPkbRating && !!isPickleballRating) {
            return formatMonthAndShortYear(player.historicPkbDoublesRatingDate);
        }
        return rating.utrType;
    };
    const historicRatingsLabel = showReliability ? getDisplayStateLabel(rating) : getHistoricRatingLabel();
    const utrLabel = typeof utrType === 'string'
        ? historicRatingsLabel
        : getLabelByTypeId(utrTypes, utrType) === 'UTR Event'
            ? 'UTR'
            : getLabelByTypeId(utrTypes, utrType);
    const doublesPicklebalLabel = player.doublesPkbRatingDisplay?.reliability || estimatedRating;
    const singlePicklebalLabel = player.singlesPkbRatingDisplay?.reliability || estimatedRating;

    const renderUtrLabel = () => {
        if (hideUtrLabel || hideReliableLabelForPkb) { return ''; }

        const label = ratingType === MatchFormatTypes.SINGLES ? singlePicklebalLabel : doublesPicklebalLabel;

        return (
            <>
                {label}  {!isItGlobalSearch && rating.value && typeDots && !rating.showPBRRating ? <span title={rating.matchType} data-testid={dataTestId && `${dataTestId}.type-dots`}>{getMatchTypeIndicator(rating)}</span> : ''}
            </>
        );
    };

    if (isPickleballRating) {
        if ((ratingType === MatchFormatTypes.SINGLES && !singlePkbRatings) || (ratingType === MatchFormatTypes.DOUBLES && !doublesPkbRatings)) {
            return '';
        }
        return renderUtrLabel();
    }

    if (isPickleBallSportType) {
        return (
            <>
                {getPickleBallRating(player, ratingType).value}
            </>
        );
    }

    return (
        <>
            {((hideUtrLabel || (utrLabel === 'UTR' && isItGlobalSearch))) ? '' : utrLabel} {!isItGlobalSearch && rating.value && typeDots && !rating.showPBRRating ? <span title={rating.matchType} data-testid={dataTestId && `${dataTestId}.type-dots`}>{getMatchTypeIndicator(rating)}</span> : ''}
        </>
    );
};

UtrLabel.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    isPickleballRating: PropTypes.bool,
    sportTypeId: PropTypes.oneOf([1, 2]).isRequired,
    isItGlobalSearch: PropTypes.bool,
    'data-testid': PropTypes.string,
};

UtrLabel.defaultProps = {
    isPickleballRating: false,
    isItGlobalSearch: false,
    'data-testid': null,
};
export default withAuth(UtrLabel);
